<script setup lang="ts">
import StyledModal from "@/components/_elements/modals/StyledModal.vue";
import { XMarkIcon } from "@heroicons/vue/20/solid";
import { useGlobalFilterStore } from "@/stores/globalFilterStore";
import { storeToRefs } from "pinia";
import { computed, ref } from "vue";

const MRI_MODALITY_SLUG = "magnetic-resonance-imaging";

const localKey = "clover.mri-interactive-images-announcement-modal";
const courseUrl = "/learn/courses/mri-cross-sectional-anatomy-pathology";

// Gated on the global filter store rather than server-side so the modal opens
// the moment someone picks MRI in the modality switcher. GlobalFilterDropdown
// only forces a navigation from course detail pages, so switching while
// already on /learn/courses never reloads and a Blade-rendered flag would go
// stale.
const globalFilterStore = useGlobalFilterStore();
const { value: activeModality } = storeToRefs(globalFilterStore);
const isMriSelected = computed(
    () => activeModality.value === MRI_MODALITY_SLUG
);

// Reactive so the modal does not reopen when the learner switches modality
// away from MRI and back again after having dismissed it.
const dismissed = ref(localStorage.getItem(localKey) === "true");

const prefersReducedMotion = window.matchMedia(
    "(prefers-reduced-motion: reduce)"
).matches;

const onClose = () => {
    dismissed.value = true;
    localStorage.setItem(localKey, "true");
};

const redirect = (to: string) => {
    onClose();
    window.location.href = to;
};
</script>

<template>
    <StyledModal
        v-if="isMriSelected"
        is-wide
        :start-open="!dismissed"
        @on-close="onClose"
    >
        <template v-slot:panel="slotProps">
            <!-- active:top-4 pins the icon in place: the global
                 `button:active { top: 2px }` rule in elements/buttons.css
                 otherwise yanks this absolutely positioned button upwards
                 while it is being clicked. -->
            <button
                class="absolute top-4 right-4 active:top-4 text-gray-800 hover:text-black"
                @click="slotProps.events?.closeModal()"
            >
                <XMarkIcon class="w-6 h-6"></XMarkIcon>
                <span class="sr-only">Close</span>
            </button>

            <p class="mt-2 mb-0 text-base font-bold text-blue-700">
                NOW AVAILABLE!
            </p>

            <h2 class="mt-1 mb-0 text-3xl leading-9 font-bold text-gray-800">
                Interactive images for MRI cross-sectional anatomy &amp;
                pathology
            </h2>

            <p class="mt-5 mb-0 text-lg leading-7 text-gray-800 text-pretty">
                We are happy to announce the launch of our new interactive
                feature for the
                <a
                    :href="courseUrl"
                    class="font-bold text-blue-700 no-underline hover:underline hover:text-blue-700"
                    @click="onClose()"
                    >MRI Cross-sectional Anatomy &amp; Pathology course</a
                >. This feature includes real-life, interactive images of
                cross-sectional anatomy and pathology, designed to enhance your
                review and memorization of MRI cross-sectional anatomy from all
                planes.
            </p>

            <img
                v-if="prefersReducedMotion"
                src="/img/mri-cross-sectional-announcement.jpg"
                class="mt-7 w-full h-auto rounded-lg shadow"
                alt="The MRI Cross-sectional Anatomy &amp; Pathology course page, showing the course overview and lesson list."
            />
            <video
                v-else
                class="mt-7 w-full h-auto rounded-lg shadow"
                poster="/img/mri-cross-sectional-announcement.jpg"
                aria-hidden="true"
                muted
                playsinline
                autoplay
                loop
            >
                <source
                    src="/video/mri-cross-sectional-announcement.mp4"
                    type="video/mp4"
                />
                <source
                    src="/video/mri-cross-sectional-announcement.webm"
                    type="video/webm"
                />
            </video>

            <div class="flex mt-10 gap-2 items-center justify-center">
                <button
                    @click="redirect(courseUrl)"
                    class="hover:cursor-pointer button no-underline bg-blue-700 text-white rounded-md border-none hover:bg-blue-800 hover:text-white"
                >
                    Access Course Now
                </button>
            </div>
        </template>
    </StyledModal>
</template>
